import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import camelcaseKeys from 'camelcase-keys';
import { SettingsItem } from '../../../services/axios/axios-api-v2-generated';
import {
    AppSettings,
    createDefaultSettings,
    SettingKeys,
    SETTINGS_DEFINITIONS,
    SettingValue,
    SyncType
} from '../../../services/settings-service';
import type { AppThunk } from '../store';

export type SettingEntry = { [K in SettingKeys]: { key: K; value: SettingValue<K> } }[SettingKeys];

//todo we should get local settings from local storage on init instead of using defaults
const initialState: AppSettings = createDefaultSettings();

export const settingsSlice = createSlice({
    name: 'settings',
    initialState: initialState,
    reducers: {
        updateSetting: (state, params: PayloadAction<SettingEntry>) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state[params.payload.key] = params.payload.value;
        },
        updateSettings: (state, params: { payload: Array<SettingEntry> }) => {
            for (const entry of params.payload) {
                // TODO: fix types
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                state[entry.key] = entry.value;
            }
        },
        initializeSettings: (state, params: { payload: SettingsItem[] }) => {
            const settings = state;
            const definitions = Object.entries(SETTINGS_DEFINITIONS);
            for (const item of params.payload) {
                if (!item.value) {
                    continue;
                }
                try {
                    const value = JSON.parse(item.value);

                    const key = definitions.find((x) => x[1].key === item.key)?.[0];
                    if (!key) {
                        continue;
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    // This is a problem, some of the values are encoded using Pascal case by .NET and most are using camel case, normalize all of them to use camel case
                    settings[key] = camelcaseKeys(value, { deep: true });
                } catch {
                    console.warn('Unable to deserialize the setting', item);
                    continue;
                }
            }
            Object.assign(state, settings);
        }
    }
});

export const initializeLocalSettings = (): AppThunk => {
    return (dispatch) => {
        const definitions = Object.entries(SETTINGS_DEFINITIONS).filter(([, value]) => value.sync === SyncType.local);
        const localSettings: SettingEntry[] = [];
        for (const [key, definition] of definitions) {
            const text = localStorage.getItem(definition.key);
            if (text === null) {
                continue;
            }
            try {
                const value = JSON.parse(text);
                // TODO: type validation would be nice
                localSettings.push({
                    key: key,
                    value: value
                } as SettingEntry);
            } catch {
                continue;
            }
        }
        dispatch(settingsSlice.actions.updateSettings(localSettings));
    };
};

export function setSetting<K extends SettingKeys>(key: K, value: SettingValue<K>) {
    return settingsSlice.actions.updateSetting({ key, value } as SettingEntry);
}

export const { updateSetting, initializeSettings } = settingsSlice.actions;
